import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, Eye, EyeOff, Gamepad2, GitBranch, Info, Layers, Loader2, Pencil, Plus, Puzzle,
  Sparkles, Trash2, TriangleAlert,
} from 'lucide-react';
import { getLessons } from '../../api/lessons';
import { getCourseModules } from '../../api/modules';
import {
  getLessonGames, createGame, deleteGame, updateGame, generateGame, getGenerationStatus, getArcadeReadiness,
} from '../../api/games';
import { unwrapList } from '../../utils/api';
import errorText from '../../utils/errorText';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import MetricCard from '../../components/MetricCard/MetricCard';
import Modal from '../../components/Modal/Modal';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import { usePermissions } from '../../hooks/usePermissions';
import StoryEditor from './StoryEditor';
import PuzzleEditor from './PuzzleEditor';
import AiGameDialog from './AiGameDialog';
import ArcadeReadiness, { WORD_GAMES } from './ArcadeReadiness';
import './CourseGames.css';

// The lesson's game list holds every GameType the API knows. Story and puzzle games are authored here;
// the four word (arcade) games are built per lesson from its vocabulary, so the page shows what their
// words still need instead of listing them; the other types have no editor yet.
const GAME_KINDS = {
  STORY_ADVENTURE: { kind: 'story', tone: 'brand' },
  PUZZLE_MASTER: { kind: 'puzzle', tone: 'purple' },
  PICTURE_QUIZ: { kind: 'auto', tone: 'teal' },
  MEMORY_MATCH: { kind: 'auto', tone: 'coral' },
  SENTENCE_BUILDER: { kind: 'auto', tone: 'info' },
  WORD_SPRINT: { kind: 'auto', tone: 'success' },
  SIMULATOR: { kind: 'other', tone: 'neutral' },
  SKILL_QUEST: { kind: 'other', tone: 'neutral' },
};

const CREATABLE_TYPES = [
  { value: 'STORY_ADVENTURE', labelKey: 'courseAssess.games.gameType.STORY_ADVENTURE' },
  { value: 'PUZZLE_MASTER', labelKey: 'courseAssess.games.gameType.PUZZLE_MASTER' },
];

const BLANK_GAME = { lessonId: '', type: 'STORY_ADVENTURE', title: '', description: '' };

/** Lessons whose games load at once; the API's short-window limit is 20 requests/second. */
const LESSON_FETCH_CONCURRENCY = 4;
/** An AI request is asked about every few seconds while the page waits for it in the foreground. */
const AI_POLL_MS = 5000;
const AI_FOREGROUND_MS = 3 * 60 * 1000;

/** Where the curriculum's lessons section opens its "add lesson" form, and a lesson's word panel. */
const ADD_LESSON_SEARCH = '?tab=curriculum&c=lessons&add=1';
const wordsSearch = (lessonId) => `?tab=curriculum&c=vocabulary&lesson=${encodeURIComponent(lessonId)}`;

const kindOf = (type) => GAME_KINDS[type]?.kind || 'other';
const isAiRequest = (game) => game.source === 'ai-request';
const isAuthored = (game) => kindOf(game.gameType) !== 'auto' && !isAiRequest(game);
const isRecent = (iso) => !!iso && Date.now() - Date.parse(iso) < AI_FOREGROUND_MS;

function GameTypeBadge({ type }) {
  const { t } = useI18n();
  const meta = GAME_KINDS[type];
  const label = meta ? t(`courseAssess.games.gameType.${type}`) : String(type || '').replace(/_/g, ' ').toLowerCase();
  return (
    <span className={`ds-badge ds-badge-${meta?.tone || 'neutral'} cg-type-badge`}>
      {meta?.kind === 'story' && <GitBranch size={12} aria-hidden="true" />}
      {meta?.kind === 'puzzle' && <Puzzle size={12} aria-hidden="true" />}
      {meta?.kind === 'auto' && <Sparkles size={12} aria-hidden="true" />}
      {label}
    </span>
  );
}

/** The one place a game is created by hand: from the header (any lesson) or from a lesson (that one). */
function NewGameDialog({ open, lessons, moduleNameOf, lessonId, saving, onClose, onCreate }) {
  const { t } = useI18n();
  const [form, setForm] = useState(BLANK_GAME);

  // A fresh form each time the dialog opens; with a single lesson there is nothing to choose
  useEffect(() => {
    if (open) setForm({ ...BLANK_GAME, lessonId: lessonId || (lessons.length === 1 ? lessons[0].id : '') });
  }, [open, lessonId, lessons]);

  const canCreate = !saving && form.lessonId && form.title.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t('courseAssess.games.newGame')}
      as="form"
      onSubmit={() => { if (canCreate) onCreate(form); }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('courseAssess.games.cancel')}</button>
          <button type="submit" className={`ds-btn ds-btn-primary ${saving ? 'is-loading' : ''}`} disabled={!canCreate}>
            {t('courseAssess.games.create')}
          </button>
        </>
      )}
    >
      <div className="cg-dialog-fields">
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-new-lesson">{t('courseAssess.games.lessonLabel')} <em className="cg-required">*</em></label>
          <Dropdown
            id="cg-new-lesson"
            value={form.lessonId}
            onChange={(val) => setForm((f) => ({ ...f, lessonId: val }))}
            options={lessons.map((lesson, idx) => {
              const moduleName = moduleNameOf(lesson.moduleId);
              return { value: lesson.id, label: `${idx + 1}. ${lesson.title}${moduleName ? ` · ${moduleName}` : ''}` };
            })}
            placeholder={t('courseAssess.games.selectLessonPlaceholder')}
            ariaLabel={t('courseAssess.games.lessonLabel')}
          />
        </div>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-new-type">{t('courseAssess.games.gameTypeLabel')}</label>
          <Dropdown
            id="cg-new-type"
            value={form.type}
            onChange={(val) => setForm((f) => ({ ...f, type: val }))}
            options={CREATABLE_TYPES.map((gt) => ({ value: gt.value, label: t(gt.labelKey) }))}
            ariaLabel={t('courseAssess.games.gameTypeLabel')}
          />
        </div>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-new-title">{t('courseAssess.games.gameTitleLabel')} <em className="cg-required">*</em></label>
          <input
            id="cg-new-title"
            className="cg-input"
            type="text"
            maxLength={200}
            data-autofocus
            placeholder={t('courseAssess.games.gameTitlePlaceholder')}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            disabled={saving}
          />
        </div>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-new-desc">{t('courseAssess.games.descriptionLabel')}</label>
          <textarea
            id="cg-new-desc"
            className="cg-textarea"
            maxLength={1000}
            rows={3}
            placeholder={t('courseAssess.games.descriptionPlaceholder')}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}

export default function CourseGames({ courseId }) {
  const { t, lang } = useI18n();
  const g = (key) => `gameAuthoring.games.${key}`;
  const navigate = useNavigate();
  const [confirm, confirmDialog] = useConfirmDialog();
  const { isAdmin, user } = usePermissions();
  // DELETE /games/:id is ADMIN-only on the API; a teacher's button would only answer 403.
  const canDeleteGame = isAdmin();
  // POST /lessons/:id/games/generate takes MENTOR and ADMIN (a teacher would get 403).
  const canUseAi = isAdmin() || String(user?.role || '').toUpperCase() === 'MENTOR';

  const [lessons, setLessons] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  // null | 'forbidden' | 'failed'
  const [loadError, setLoadError] = useState(null);
  // lessonId → { status: 'loading' | 'ready' | 'failed', games }
  const [gamesByLesson, setGamesByLesson] = useState({});
  // The word-game checklist of every lesson, from one request
  const [readiness, setReadiness] = useState({ status: 'loading', rules: null, byLesson: {} });
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [expandedGame, setExpandedGame] = useState(null);
  // The dialogs: closed (null), or open for one lesson ('' = the person picks it)
  const [newGameFor, setNewGameFor] = useState(null);
  const [aiFor, setAiFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [startingAi, setStartingAi] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  // AI request id → 'polling' | 'idle' (no longer waited for; "Check" asks again)
  const [aiPolls, setAiPolls] = useState({});
  // What an open editor reported: gameId → { nodes, hasStart } | { puzzles }
  const [stats, setStats] = useState({});
  const timers = useRef(new Map());
  const alive = useRef(true);
  const checkAiRef = useRef(null);

  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      pending.forEach((id) => clearTimeout(id));
      pending.clear();
    };
  }, []);

  const setLessonGames = useCallback((lessonId, update) => {
    setGamesByLesson((prev) => {
      const entry = prev[lessonId] || { status: 'ready', games: [] };
      return { ...prev, [lessonId]: { ...entry, ...update(entry) } };
    });
  }, []);

  const patchGame = useCallback((lessonId, gameId, patch) => {
    setLessonGames(lessonId, (entry) => ({ games: entry.games.map((game) => (game.id === gameId ? { ...game, ...patch } : game)) }));
  }, [setLessonGames]);

  const loadLessonGames = useCallback(async (lessonId) => {
    setLessonGames(lessonId, () => ({ status: 'loading' }));
    try {
      const res = await getLessonGames(lessonId);
      const games = Array.isArray(res) ? res : unwrapList(res);
      if (alive.current) setLessonGames(lessonId, () => ({ status: 'ready', games }));
      return games;
    } catch {
      // Never "0 games" for a read that failed: the lesson says so and offers a retry
      if (alive.current) setLessonGames(lessonId, () => ({ status: 'failed', games: [] }));
      return null;
    }
  }, [setLessonGames]);

  const loadReadiness = useCallback(async () => {
    setReadiness((r) => ({ ...r, status: 'loading' }));
    try {
      const res = await getArcadeReadiness(courseId);
      const byLesson = {};
      (res?.lessons || []).forEach((l) => { byLesson[l.lessonId] = l; });
      if (alive.current) setReadiness({ status: 'ready', rules: res?.rules || null, byLesson });
    } catch {
      if (alive.current) setReadiness({ status: 'failed', rules: null, byLesson: {} });
    }
  }, [courseId]);

  /**
   * Ask where an AI request stands. Ready: reload the lesson and open the new game for review.
   * Failed: drop the request. Pending: ask again in a few seconds while the page still waits.
   */
  const checkAiRequest = async (lessonId, requestId, { keepPolling = true, startedAt = Date.now(), manual = false } = {}) => {
    clearTimeout(timers.current.get(requestId));
    timers.current.delete(requestId);
    setAiPolls((p) => ({ ...p, [requestId]: 'polling' }));
    let result;
    try {
      result = await getGenerationStatus(requestId);
    } catch {
      result = { status: 'unknown' };
    }
    if (!alive.current) return;
    const forget = () => setAiPolls((p) => {
      const next = { ...p };
      delete next[requestId];
      return next;
    });

    if (result?.status === 'ready') {
      forget();
      await loadLessonGames(lessonId);
      if (!alive.current) return;
      setExpandedLesson(lessonId);
      setExpandedGame(result.gameId);
      toast.success(t(g('aiReady')));
      return;
    }
    if (result?.status === 'failed') {
      forget();
      setLessonGames(lessonId, (entry) => ({ games: entry.games.filter((game) => game.id !== requestId) }));
      toast.error(t(g('aiFailed')));
      return;
    }
    if (keepPolling && Date.now() - startedAt < AI_FOREGROUND_MS) {
      timers.current.set(requestId, setTimeout(() => {
        checkAiRef.current?.(lessonId, requestId, { keepPolling, startedAt });
      }, AI_POLL_MS));
      return;
    }
    setAiPolls((p) => ({ ...p, [requestId]: 'idle' }));
    if (manual) toast(t(g('aiStillWorking')));
  };
  checkAiRef.current = checkAiRequest;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    let lessonList;
    try {
      const [lessonsRes, modulesRes] = await Promise.all([
        getLessons(courseId, { limit: 200 }),
        getCourseModules(courseId, { limit: 200 }),
      ]);
      lessonList = unwrapList(lessonsRes);
      setLessons(lessonList);
      setModules(unwrapList(modulesRes));
      setGamesByLesson(Object.fromEntries(lessonList.map((l) => [l.id, { status: 'loading', games: [] }])));
    } catch (err) {
      setLoadError(err.code === 403 ? 'forbidden' : 'failed');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (lessonList.length === 0) return;
    loadReadiness();

    // A course can hold dozens of lessons and the API throttles bursts, so their games load a
    // few at a time and each lesson shows its own result as it arrives.
    const queue = [...lessonList];
    await Promise.all(Array.from({ length: Math.min(LESSON_FETCH_CONCURRENCY, queue.length) }, async () => {
      while (queue.length > 0 && alive.current) {
        const lesson = queue.shift();
        const games = await loadLessonGames(lesson.id);
        // An AI request still open from earlier: see whether its game has arrived
        (games || []).filter(isAiRequest).forEach((game) => {
          checkAiRef.current?.(lesson.id, game.id, { keepPolling: isRecent(game.createdAt) });
        });
      }
    }));
  }, [courseId, loadLessonGames, loadReadiness]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getModuleName = (moduleId) => {
    const m = modules.find((mod) => mod.id === moduleId);
    return m ? m.title : '';
  };

  const handleCreate = async (form) => {
    setCreating(true);
    try {
      const game = await createGame(form.lessonId, {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
      });
      setLessonGames(form.lessonId, (entry) => ({
        games: [...entry.games, { source: 'staff', isActive: true, _count: { nodes: 0, puzzles: 0, attempts: 0 }, ...game }],
      }));
      setNewGameFor(null);
      // Show the new game where it lives, ready to be built
      setExpandedLesson(form.lessonId);
      setExpandedGame(game.id);
      toast.success(t('courseAssess.games.gameCreated'));
    } catch (err) {
      toast.error(errorText(err, t('courseAssess.games.createGameFailed')));
    } finally {
      setCreating(false);
    }
  };

  const handleStartAi = async ({ lessonId, gameType, topic }) => {
    setStartingAi(true);
    try {
      const res = await generateGame(lessonId, { gameType, topic });
      setLessonGames(lessonId, (entry) => ({
        games: [...entry.games, {
          id: res.gameId,
          name: `AI: ${topic}`,
          gameType,
          source: 'ai-request',
          isActive: false,
          createdAt: res.requestedAt,
          _count: { nodes: 0, puzzles: 0, attempts: 0 },
        }],
      }));
      setAiFor(null);
      setExpandedLesson(lessonId);
      toast.success(t(g('aiStarted')));
      checkAiRequest(lessonId, res.gameId, { keepPolling: true });
    } catch (err) {
      // 503: the kill switch is off or the job queue is unreachable
      toast.error(err.code === 503 ? t(g('aiUnavailable')) : errorText(err, t(g('aiStartFailed'))));
    } finally {
      setStartingAi(false);
    }
  };

  const handleDelete = async (gameId, lessonId) => {
    const ok = await confirm({
      title: t('courseAssess.games.deleteTitle'),
      description: t('courseAssess.games.deleteDesc'),
      confirmLabel: t('courseAssess.games.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(gameId);
    try {
      await deleteGame(gameId);
      setLessonGames(lessonId, (entry) => ({ games: entry.games.filter((game) => game.id !== gameId) }));
      if (expandedGame === gameId) setExpandedGame(null);
      toast.success(t('courseAssess.games.gameDeleted'));
    } catch (err) {
      toast.error(errorText(err, t('courseAssess.games.deleteGameFailed')));
    } finally {
      setDeleting(null);
    }
  };

  const handleVisibility = async (lessonId, game, isActive) => {
    setToggling(game.id);
    try {
      await updateGame(game.id, { isActive });
      patchGame(lessonId, game.id, { isActive });
      toast.success(t(g(isActive ? 'published' : 'unpublished')));
    } catch (err) {
      toast.error(errorText(err, t(g('visibilityFailed'))));
    } finally {
      setToggling(null);
    }
  };

  const handleStats = (lessonId, game, next) => {
    setStats((prev) => ({ ...prev, [game.id]: next }));
    const count = game._count || {};
    patchGame(lessonId, game.id, {
      _count: { ...count, nodes: next.nodes ?? count.nodes ?? 0, puzzles: next.puzzles ?? count.puzzles ?? 0 },
    });
  };

  const readyWordGames = (lessonId) => {
    const r = readiness.byLesson[lessonId];
    return r ? (r.games || []).filter((x) => x.available).length : 0;
  };

  const lessonEntries = lessons.map((lesson) => ({ lesson, entry: gamesByLesson[lesson.id] || { status: 'loading', games: [] } }));
  const authoredCount = lessonEntries.reduce((sum, { entry }) => sum + entry.games.filter(isAuthored).length, 0);
  const autoCount = readiness.status === 'ready'
    ? lessons.reduce((sum, l) => sum + readyWordGames(l.id), 0)
    : lessonEntries.reduce((sum, { entry }) => sum + entry.games.filter((game) => kindOf(game.gameType) === 'auto').length, 0);
  const lessonsWithGames = lessonEntries.filter(({ lesson, entry }) => entry.games.some(isAuthored) || readyWordGames(lesson.id) > 0);

  if (loading) {
    return (
      <div className="cg-page">
        <LoadingState skeleton="page" message={t('courseAssess.games.loadingGames')} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="cg-page">
        {loadError === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('courseAssess.games.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={fetchAll} />
        )}
      </div>
    );
  }

  const renderGame = (lesson, game) => {
    const kind = kindOf(game.gameType);
    const open = expandedGame === game.id;

    if (isAiRequest(game)) {
      const polling = aiPolls[game.id] === 'polling';
      return (
        <div key={game.id} className="cg-game cg-game--pending">
          <div className="cg-game-header">
            <span className="cg-game-toggle cg-game-toggle--static">
              <span className="cg-game-name">{game.name}</span>
              <GameTypeBadge type={game.gameType} />
              <span className="ds-badge ds-badge-purple">
                <Loader2 size={12} className={polling ? 'cg-spin' : ''} aria-hidden="true" />
                {t(g('aiPending'))}
              </span>
            </span>
            <button
              type="button"
              className={`ds-btn ds-btn-secondary ds-btn-sm ${polling ? 'is-loading' : ''}`}
              onClick={() => checkAiRequest(lesson.id, game.id, { keepPolling: true, manual: true })}
              disabled={polling}
            >
              {t(g('aiCheck'))}
            </button>
          </div>
          <p className="cg-game-desc">{t(g('aiPendingDesc'))}</p>
        </div>
      );
    }

    const nodeCount = kind === 'story' ? game._count?.nodes : 0;
    const puzzleCount = kind === 'puzzle' ? game._count?.puzzles : 0;
    const editable = kind === 'story' || kind === 'puzzle';
    const s = stats[game.id];
    const complete = kind === 'story' ? !!s && s.nodes > 0 && s.hasStart : !!s && s.puzzles > 0;

    return (
      <div key={game.id} className="cg-game">
        <div className="cg-game-header">
          <button
            type="button"
            className="cg-game-toggle"
            onClick={() => setExpandedGame(open ? null : game.id)}
            aria-expanded={open}
          >
            <span className="cg-game-name">{game.name || game.title}</span>
            <GameTypeBadge type={game.gameType} />
            {game.source === 'ai' && <span className="ds-badge ds-badge-purple"><Sparkles size={12} aria-hidden="true" />{t(g('aiBadge'))}</span>}
            {editable && game.isActive === false && (
              <span className="ds-badge ds-badge-neutral"><EyeOff size={12} aria-hidden="true" />{t(g('hiddenBadge'))}</span>
            )}
            {nodeCount > 0 && (
              <span className="cg-game-meta">{t(pluralKey('courseAssess.games.nodesMeta', nodeCount, lang), { count: nodeCount })}</span>
            )}
            {puzzleCount > 0 && (
              <span className="cg-game-meta">{t(pluralKey('courseAssess.games.puzzlesMeta', puzzleCount, lang), { count: puzzleCount })}</span>
            )}
            <ChevronDown className={`cg-chevron ${open ? 'is-open' : ''}`} size={16} aria-hidden="true" />
          </button>
          {canDeleteGame && (
            <button
              type="button"
              className="ui-icon-btn ui-icon-btn--danger"
              onClick={() => handleDelete(game.id, lesson.id)}
              disabled={deleting === game.id}
              aria-label={t('courseAssess.games.delete')}
              title={t('courseAssess.games.delete')}
            >
              <Trash2 aria-hidden="true" />
            </button>
          )}
        </div>
        {game.description && <p className="cg-game-desc">{game.description}</p>}

        {open && (
          <div className="cg-game-editor">
            {editable && (
              <div className="cg-visibility">
                {game.source === 'ai' && game.isActive === false && (
                  <div className="ui-alert" data-tone="info">
                    <Sparkles aria-hidden="true" />
                    <p>{t(g('aiReview'))}</p>
                  </div>
                )}
                <div className="cg-visibility-row">
                  {game.isActive === false ? (
                    <>
                      <span className="cg-visibility-state"><EyeOff size={14} aria-hidden="true" />{t(g('hiddenBadge'))}</span>
                      <button
                        type="button"
                        className={`ds-btn ds-btn-primary ds-btn-sm ${toggling === game.id ? 'is-loading' : ''}`}
                        onClick={() => handleVisibility(lesson.id, game, true)}
                        disabled={toggling === game.id || !complete}
                        title={!complete ? t(kind === 'story' ? g('noStart') : 'courseAssess.games.noPuzzlesHint') : undefined}
                      >
                        <Eye aria-hidden="true" />
                        {t(g('publish'))}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="cg-visibility-state is-visible"><Eye size={14} aria-hidden="true" />{t(g('visibleBadge'))}</span>
                      <button
                        type="button"
                        className={`ds-btn ds-btn-ghost ds-btn-sm ${toggling === game.id ? 'is-loading' : ''}`}
                        onClick={() => handleVisibility(lesson.id, game, false)}
                        disabled={toggling === game.id}
                      >
                        <EyeOff aria-hidden="true" />
                        {t(g('unpublish'))}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            {kind === 'story' && <StoryEditor game={game} onStats={(next) => handleStats(lesson.id, game, next)} />}
            {kind === 'puzzle' && <PuzzleEditor game={game} onStats={(next) => handleStats(lesson.id, game, next)} />}
            {(kind === 'auto' || kind === 'other') && (
              <div className="ui-alert" data-tone="info">
                <Info aria-hidden="true" />
                <p>{t(kind === 'auto' ? 'courseAssess.games.autoNote' : 'courseAssess.games.notEditableNote')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="cg-page">
      <div className="cg-intro">
        <div className="cg-intro-text">
          <p className="cg-lead">{t('courseAssess.games.pageSubtitle')}</p>
          <p className="cg-auto-explain"><Sparkles size={14} aria-hidden="true" /> {t(g('autoExplain'))}</p>
        </div>
        <div className="cg-intro-actions">
          {canUseAi && (
            <button
              type="button"
              className="ds-btn ds-btn-secondary cg-create"
              onClick={() => setAiFor('')}
              disabled={lessons.length === 0}
            >
              <Sparkles aria-hidden="true" />
              {t(g('aiButton'))}
            </button>
          )}
          <button
            type="button"
            className="ds-btn ds-btn-primary cg-create"
            onClick={() => setNewGameFor('')}
            disabled={lessons.length === 0}
            title={lessons.length === 0 ? t('courseAssess.games.noLessonsDesc') : undefined}
          >
            <Plus aria-hidden="true" />
            {t('courseAssess.games.addGame')}
          </button>
        </div>
      </div>

      <div className="kpi-row">
        <MetricCard countUp={false} title={t('courseAssess.games.kpiTotal')} value={authoredCount + autoCount} icon={<Gamepad2 />} tone="info" />
        {/* "1 / 2": of all the lessons, how many have a game (a bare 1 reads as the whole course) */}
        <MetricCard countUp={false} title={t('courseAssess.games.kpiLessons')} value={`${lessonsWithGames.length} / ${lessons.length}`} icon={<Layers />} tone="success" />
        <MetricCard countUp={false} title={t('courseAssess.games.kpiAuthored')} value={authoredCount} icon={<Pencil />} tone="brand" />
        <MetricCard countUp={false} title={t('courseAssess.games.kpiAuto')} value={autoCount} icon={<Sparkles />} tone="teal" />
      </div>

      {lessons.length === 0 && (
        <PageEmptyState
          variant="docs"
          size="sm"
          title={t('courseAssess.games.noLessonsTitle')}
          description={t('courseAssess.games.noLessonsDesc')}
          actionLabel={t('gameAuthoring.addLesson')}
          onAction={() => navigate({ search: ADD_LESSON_SEARCH })}
        />
      )}

      <div className="cg-lessons-list">
        {lessonEntries.map(({ lesson, entry }, idx) => {
          const games = entry.games.filter((game) => kindOf(game.gameType) !== 'auto');
          const authored = games.filter(isAuthored).length;
          const isExpanded = expandedLesson === lesson.id;
          const moduleName = getModuleName(lesson.moduleId);
          const bodyId = `cg-lesson-${lesson.id}`;
          const lessonReadiness = readiness.byLesson[lesson.id];

          return (
            <section key={lesson.id} className={`section-card cg-lesson ${isExpanded ? 'cg-lesson-expanded' : ''}`}>
              <button
                type="button"
                className="cg-lesson-hd"
                onClick={() => setExpandedLesson(isExpanded ? null : lesson.id)}
                aria-expanded={isExpanded}
                aria-controls={bodyId}
              >
                <span className="cg-lesson-num">{idx + 1}</span>
                <span className="cg-lesson-meta">
                  <span className="cg-lesson-name">{lesson.title}</span>
                  {moduleName && <span className="cg-lesson-module">{moduleName}</span>}
                </span>
                {lessonReadiness && (
                  <span className="cg-word-games" title={t(g('readinessTitle'))}>
                    <Sparkles size={12} aria-hidden="true" />
                    {readyWordGames(lesson.id)}/{WORD_GAMES.length}
                  </span>
                )}
                {/* Every lesson says how many games it has, so one with none is not mistaken for one not looked at */}
                {entry.status === 'loading' && (
                  <span className="ds-badge ds-badge-neutral cg-games-count" aria-label={t('courseAssess.games.loadingGames')}>…</span>
                )}
                {entry.status === 'failed' && (
                  <span className="ds-badge ds-badge-warning cg-games-count">{t(g('loadFailedShort'))}</span>
                )}
                {entry.status === 'ready' && (
                  <span className={`ds-badge ${authored > 0 ? 'ds-badge-info' : 'ds-badge-neutral'} cg-games-count`}>
                    {t(pluralKey('courseAssess.games.gameCountLabel', authored, lang), { count: authored })}
                  </span>
                )}
                <ChevronDown className={`cg-chevron ${isExpanded ? 'is-open' : ''}`} size={18} aria-hidden="true" />
              </button>

              {isExpanded && (
                <div className="cg-lesson-body" id={bodyId}>
                  <div className="cg-lesson-toolbar">
                    <span className="cg-toolbar-label">{t(pluralKey('courseAssess.games.gamesInLesson', authored, lang), { count: authored })}</span>
                    <span className="cg-toolbar-actions">
                      {canUseAi && (
                        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setAiFor(lesson.id)}>
                          <Sparkles aria-hidden="true" />
                          {t(g('aiButton'))}
                        </button>
                      )}
                      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setNewGameFor(lesson.id)}>
                        <Plus aria-hidden="true" />
                        {t('courseAssess.games.newGame')}
                      </button>
                    </span>
                  </div>

                  {entry.status === 'failed' && (
                    <div className="ui-alert" data-tone="danger" role="alert">
                      <TriangleAlert aria-hidden="true" />
                      <p>{t(g('lessonLoadFailed'))}</p>
                      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => loadLessonGames(lesson.id)}>
                        {t('ui.retry')}
                      </button>
                    </div>
                  )}
                  {entry.status === 'loading' && (
                    <p className="cg-empty-hint">{t('courseAssess.games.loadingGames')}</p>
                  )}
                  {entry.status === 'ready' && games.length === 0 && (
                    <InlineEmpty kind="game" title={t('courseAssess.games.noGamesForLesson')} />
                  )}

                  <div className="cg-games-list">
                    {games.map((game) => renderGame(lesson, game))}
                  </div>

                  {lessonReadiness && (
                    <ArcadeReadiness
                      lesson={lessonReadiness}
                      rules={readiness.rules}
                      onOpenWords={() => navigate({ search: wordsSearch(lesson.id) })}
                    />
                  )}
                  {readiness.status === 'failed' && (
                    <div className="ui-alert" data-tone="warning" role="status">
                      <TriangleAlert aria-hidden="true" />
                      <p>{t(g('readinessFailed'))}</p>
                      <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={loadReadiness}>
                        {t('ui.retry')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <NewGameDialog
        open={newGameFor !== null}
        lessons={lessons}
        moduleNameOf={getModuleName}
        lessonId={newGameFor}
        saving={creating}
        onClose={() => setNewGameFor(null)}
        onCreate={handleCreate}
      />
      {canUseAi && (
        <AiGameDialog
          open={aiFor !== null}
          lessons={lessons}
          moduleNameOf={getModuleName}
          lessonId={aiFor}
          saving={startingAi}
          onClose={() => setAiFor(null)}
          onStart={handleStartAi}
        />
      )}
      {confirmDialog}
    </div>
  );
}
